import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, BuildingOfficeIcon } from '@heroicons/react/24/solid';
import { addToast, Alert, Button } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '~/hooks/useAuth';
import { useRemoveMember } from '~/hooks/useMembers';
import { usePermission } from '~/hooks/usePermission';
import { useTenant } from '~/hooks/useTenant';
import { updateOrganization } from '~/services/api/organizations';
import { clearActiveOrgId } from '~/utils/activeOrg';
import { logger } from '~/utils/logger';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { Input, Textarea } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';

import { type OrgUpdateFormData, orgUpdateSchema } from './schemas';

/**
 * Format ISO date string to user-friendly display format.
 */
function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function OrganizationSettings(): JSX.Element {
  const { currentOrg } = useTenant();
  const { canEditOrg } = usePermission();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const removeMember = useRemoveMember();
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
  } = useForm<OrgUpdateFormData>({
    resolver: zodResolver(orgUpdateSchema),
    mode: 'onBlur',
    defaultValues: {
      name: currentOrg?.name ?? '',
      description: currentOrg?.description ?? '',
    },
  });

  // Sync form state with org data when it changes
  useEffect(() => {
    if (currentOrg) {
      reset({
        name: currentOrg.name,
        description: currentOrg.description ?? '',
      });
    }
  }, [currentOrg, reset]);

  const { isPending, mutate } = useMutation({
    mutationKey: ['updateOrganization', currentOrg?.id],
    mutationFn: async (data: OrgUpdateFormData) => {
      if (!currentOrg) throw new Error('No organization selected');
      return updateOrganization(currentOrg.id, data);
    },
    onSuccess: () => {
      addToast({
        description: 'Organization updated successfully!',
        color: 'success',
        icon: <BuildingOfficeIcon />,
      });
      // Invalidate organization queries to refresh context
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: (error: Error) => {
      addToast({
        description: error.message || 'Failed to update organization',
        color: 'danger',
      });
    },
  });

  const onSubmit = (data: OrgUpdateFormData): void => {
    mutate(data);
  };

  const handleLeave = useCallback(() => {
    if (!user?.uid || !currentOrg) return;

    removeMember.mutate(
      {
        orgId: currentOrg.id,
        userId: user.uid,
      },
      {
        onSuccess: () => {
          logger.info({ component: 'OrganizationSettings' }, 'Successfully left organization', {
            orgId: currentOrg.id,
          });

          // Clear the active org ID from localStorage since we're leaving
          clearActiveOrgId();

          // Explicitly invalidate organizations query to refresh org selector
          void queryClient.invalidateQueries({ queryKey: ['organization'] });

          addToast({
            description: `You have left ${currentOrg.name}`,
            color: 'success',
          });

          setShowLeaveModal(false);

          // Navigate to home - TenantContext will handle org switching or onboarding redirect
          void navigate('/', { replace: true });
        },
        onError: (error: Error) => {
          logger.error(
            { component: 'OrganizationSettings' },
            'Failed to leave organization',
            error,
          );

          // Handle specific error cases
          if (error.message.includes('last member')) {
            addToast({
              description:
                'You are the last member. Please delete the organization from the Danger Zone instead.',
              color: 'danger',
            });
          } else if (error.message.includes('last owner')) {
            addToast({
              description:
                'You are the last owner. Transfer ownership to another member on the Members page, or delete the organization from the Danger Zone.',
              color: 'danger',
            });
          } else if (error.message.includes('403')) {
            addToast({
              description: 'You do not have permission to leave this organization.',
              color: 'danger',
            });
          } else {
            addToast({
              description: error.message || 'Failed to leave organization.',
              color: 'danger',
            });
          }
        },
      },
    );
  }, [user?.uid, currentOrg, removeMember, navigate, queryClient]);

  if (!currentOrg) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-8">
      <form noValidate onSubmit={e => void handleSubmit(onSubmit)(e)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium mb-2">Slug</p>
            <p className="px-3 py-2 bg-surface-elevated rounded-lg ">{currentOrg.slug}</p>
            <p className="text-xs mt-1">Used in URLs and API calls. Cannot be changed.</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-2">Created</p>
            <div className="px-3 py-2 bg-surface-elevated rounded-lg">
              {formatDate(currentOrg.created_at)}
            </div>
          </div>
        </div>
        <Input
          id="name"
          labelPlacement="outside-top"
          label="Organization Name"
          placeholder="Enter organization name"
          description="The display name for your organization."
          isInvalid={!!errors.name}
          errorMessage={errors.name?.message}
          isDisabled={!canEditOrg}
          {...register('name')}
        />

        <Textarea
          id="description"
          label="Description"
          placeholder="Describe your organization (optional)"
          description="A brief description of what your organization does."
          isInvalid={!!errors.description}
          errorMessage={errors.description?.message}
          isDisabled={!canEditOrg}
          {...register('description')}
        />

        {canEditOrg && (
          <Button type="submit" color="primary" isDisabled={isPending || !isDirty}>
            {isPending ? <LoadingSpinner /> : 'Save Changes'}
          </Button>
        )}

        {!canEditOrg && (
          <p className="text-sm">
            You need owner or admin permissions to edit organization details.
          </p>
        )}
      </form>

      <div className="border-t border-default pt-4">
        <h3 className="text-lg font-semibold mb-2">Leave Organization</h3>
        <p className="text-sm">
          Remove yourself from this organization. You will lose access to all projects and teams.
        </p>
        {currentOrg.role === 'owner' && (
          <Alert
            className="bg-transparent text-warning border border-warning p-1 my-4 text-sm"
            color="warning"
            variant="solid"
          >
            As an owner, you can only leave if there is another owner, or you must delete the
            organization from the Danger Zone.
          </Alert>
        )}
        <Button
          color="danger"
          onPress={() => setShowLeaveModal(true)}
          startContent={<ArrowRightOnRectangleIcon className="w-4 h-4" />}
        >
          Leave {currentOrg.name}
        </Button>
      </div>

      {/* Leave Organization Confirmation */}
      <ConfirmationDialog
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onConfirm={handleLeave}
        isPending={removeMember.isPending}
        title="Leave Organization"
        confirmText="Leave Organization"
        confirmColor="danger"
        content={
          <div className="flex flex-col gap-2">
            <p>
              Are you sure you want to leave <strong>{currentOrg.name}</strong>?
            </p>
            <p className="text-sm text-foreground-500">
              You will immediately lose access to all projects, tasks, and teams in this
              organization. If you need access again, you&apos;ll need to be re-invited.
            </p>
          </div>
        }
      />
    </div>
  );
}
